import { useLazyQuery } from '@apollo/client/react';
import pDebounce from 'p-debounce';
import { useEffect } from 'react';

import type { SelectOption } from '@zen/DesignSystem/types';
import type { EntitledAccountsPayload, OrganizationalRoleEnum } from '@zen/graphql/types.generated';

import {
  getAccountOrganizationalRole,
  getEntitledManageableAccountOptions
} from '../AccountDetails/AccountLocations/AddLocationModal/helpers';
import {
  GetEntitledAccountsDocument,
  type GetEntitledAccountsQueryResult,
  type GetEntitledAccountsQueryVariables
} from '../AccountDetails/graphql';

type EntitledAccountOptionsArguments = {
  accountId: string;
  selectedAccountId?: string;
};

export const useEntitledAccountOptions = (args: EntitledAccountOptionsArguments) => {
  const { accountId, selectedAccountId } = args;
  const [fetchEntitledAccounts, { data, loading, error }] = useLazyQuery<
    GetEntitledAccountsQueryResult,
    GetEntitledAccountsQueryVariables
  >(GetEntitledAccountsDocument, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    fetchEntitledAccounts({
      variables: {
        accountId,
        searchQuery: '',
        selectedAccountId
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (query: string): void => {
    if (query) {
      fetchEntitledAccounts({
        variables: {
          accountId,
          searchQuery: query
        }
      });
    }
  };

  const debounceDelay: number = 300;
  const debouncedHandleInputChange = pDebounce(handleInputChange, debounceDelay);

  const options: SelectOption<string>[] =
    getEntitledManageableAccountOptions(data?.entitledAccounts as EntitledAccountsPayload) || [];

  const getOrganizationalRole = (id: string): OrganizationalRoleEnum | undefined => {
    return getAccountOrganizationalRole(data?.entitledAccounts as EntitledAccountsPayload, id);
  };

  return {
    options,
    loading,
    error,
    onSearch: debouncedHandleInputChange,
    getOrganizationalRole
  };
};
